// Prices and the shop gate (IMPLEMENTATION §0.3, GDD §6 "Prices", M1 decision 12). Prices come from the
// compiled tables only: prices[n] with n = L1-equivalents owned (a level-L copy counts 2^(L−1); deposits per
// ore, drills globally, machines per type; ghosts count, they are paid for) minus the item's freeCopies.
// Past the end of a table the item is sold out. Chutes are one flat price.
// The shop gate is the unlock check: the `shop` effect (u04) plus the item's own effect (`shop.deposit.<ore>`
// / `build.<type>`). Visibility (M4a, GDD §7 "a deposit becomes visible in the Shop at 50% of its price",
// economy.json shopVisibleAt for every item): an unlocked item is REVEALED the first time cash reaches
// shopVisiblePermille of its current price, and stays visible (progress.seen, sticky; phase 7) so a card never
// disappears after a purchase raises its price. Affordable implies visible, so buying needs no extra gate.

import { shopItem } from '../bdefs.ts';
import type { RejectReason } from '../commands.ts';
import { floorDiv } from '../core/num.ts';
import type { Int } from '../core/types.ts';
import { isActive, type ShopItemDef, type SimDefs } from '../defs.ts';
import type { EffectId, ShopId } from '../gen/ids.ts';
import type { SimState } from '../state.ts';
import { hasEffect } from '../unlocks.ts';

/** L1-equivalents of this shop item currently owned (2^(level−1) each), ghosts included. */
export function ownedL1(s: SimState, item: ShopItemDef): Int {
  let n = 0;
  for (const b of s.buildings) {
    const match =
      item.kind === 'deposit' ? b.type === 'deposit' && b.ore === item.ref : b.type === item.ref;
    if (!match) continue;
    let eq = 1;
    for (let l = 1; l < b.level; l++) eq *= 2;
    n += eq;
  }
  return n;
}

/** Price of the next copy, or null when sold out. */
export function priceOf(s: SimState, d: SimDefs, id: ShopId): Int | null {
  const item = shopItem(d, id);
  if (item.flat) return item.prices[0] ?? null;
  let n = ownedL1(s, item) - item.freeCopies;
  if (n < 0) n = 0;
  return item.prices[n] ?? null;
}

/** The unlock effect that opens a shop item. */
export function shopEffect(item: ShopItemDef): EffectId {
  return (item.kind === 'deposit' ? `shop.deposit.${item.ref}` : `build.${item.ref}`) as EffectId;
}

/** Why the shop item can't be bought at all now (version, unlocks), or null. */
export function shopBlock(s: SimState, d: SimDefs, id: ShopId): RejectReason | null {
  let item: ShopItemDef;
  try {
    item = shopItem(d, id);
  } catch {
    return 'stale';
  }
  if (!isActive(d, item.version)) return 'notUnlocked';
  if (!hasEffect(s, d, 'shop') || !hasEffect(s, d, shopEffect(item))) return 'notUnlocked';
  return null;
}

/** Cash needed to reveal an item at `price`: ceil(price × shopVisiblePermille / 1000) (price ≤ $1T ceiling). */
export function revealCash(d: SimDefs, price: Int): Int {
  return floorDiv(price * d.economy.shopVisiblePermille + 999, 1000);
}

/** Cash has reached the reveal share of the price. */
export function nearPrice(s: SimState, d: SimDefs, price: Int): boolean {
  return s.wallet.cash >= revealCash(d, price);
}

/** The shop item the goal chip currently prices (`unlocks.json goal.shop` of the current goal), or null. */
export function goalShopItem(s: SimState, d: SimDefs): ShopId | null {
  const g = d.unlocks[s.progress.goal]?.goal ?? null;
  return g !== null && g.type === 'shop' ? g.id : null;
}

/**
 * Due for the Shop: unlocked and either the goal chip's shop target (the chip never points at a hidden
 * card — UX §4 "UNLOCKED: Caster" drops from the chip with a Shop badge) or cash ≥ the reveal share of its
 * price (GDD §7's 50% rule, applied to every item).
 */
export function revealDue(s: SimState, d: SimDefs, id: ShopId): boolean {
  if (shopBlock(s, d, id) !== null) return false;
  if (goalShopItem(s, d) === id) return true;
  const price = priceOf(s, d, id);
  return price !== null && nearPrice(s, d, price);
}

/** Shown in the shop now: unlocked and (revealed before, or due now). */
export function shopVisible(s: SimState, d: SimDefs, id: ShopId): boolean {
  if (shopBlock(s, d, id) !== null) return false;
  return s.progress.seen.includes(id) || revealDue(s, d, id);
}
